// Browser audio for the avatar simulator. The bridge and bot speak 8 kHz
// 16-bit mono PCM; the browser's AudioContext runs at its native rate (44.1 or
// 48 kHz — an 8 kHz context can't take a live mic in Firefox), so the worklets
// resample:
//   mic → capture worklet (→ 8 kHz Int16, 40 ms chunks) → onPcm(ArrayBuffer)   (to the bridge)
//   bridge PCM → player worklet (jitter buffer, 8 kHz → native) → speakers
// Echo cancellation is requested on the mic so the bot doesn't hear itself
// through the speakers (headphones are still the most reliable).

const CAPTURE = `
// Mic → 8 kHz: windowed-sinc low-pass at 3.7 kHz (the phone band) evaluated at
// each 8 kHz output instant — a proper resampler, so speech energy above 4 kHz
// can't alias back into the voice (a plain average leaves a metallic edge).
class SimCapture extends AudioWorkletProcessor {
  constructor() {
    super();
    this.ratio = sampleRate / 8000;               // input samples per output sample
    this.W = Math.ceil(10 * this.ratio);          // kernel half-width (input samples): sharp edge
    this.P = 128;                                 // fractional-phase table resolution
    const fc = 3700 / sampleRate, taps = 2 * this.W;
    this.tab = [];
    for (let p = 0; p <= this.P; p++) {
      const row = new Float32Array(taps); let sum = 0;
      for (let j = 0; j < taps; j++) {
        const x = (j - this.W + 1) - p / this.P;  // distance from the output instant
        const sinc = x === 0 ? 1 : Math.sin(2 * Math.PI * fc * x) / (2 * Math.PI * fc * x);
        const t = (x + this.W) / (2 * this.W);    // Blackman window over [-W, W]
        const win = t <= 0 || t >= 1 ? 0 : 0.42 - 0.5 * Math.cos(2 * Math.PI * t) + 0.08 * Math.cos(4 * Math.PI * t);
        row[j] = sinc * win; sum += row[j];
      }
      for (let j = 0; j < taps; j++) row[j] /= sum;   // unity gain at DC
      this.tab.push(row);
    }
    this.ring = new Float32Array(8192); this.mask = 8191;
    this.inCount = 0;                             // input samples received so far
    this.nextOut = this.W;                        // input-sample position of the next output
    this.buf = new Int16Array(320); this.n = 0; this.peak = 0;
  }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (!ch) return true;
    for (let i = 0; i < ch.length; i++) this.ring[(this.inCount + i) & this.mask] = ch[i];
    this.inCount += ch.length;
    const W = this.W, taps = 2 * W;
    while (this.nextOut + W < this.inCount) {
      let base = Math.floor(this.nextOut), p = Math.round((this.nextOut - base) * this.P);
      if (p === this.P) { base++; p = 0; }
      const row = this.tab[p], start = base - W + 1;
      let acc = 0;
      for (let j = 0; j < taps; j++) acc += this.ring[(start + j) & this.mask] * row[j];
      this.nextOut += this.ratio;
      const v = Math.max(-1, Math.min(1, acc));
      this.buf[this.n++] = v < 0 ? v * 0x8000 : v * 0x7fff;
      const a = v < 0 ? -v : v; if (a > this.peak) this.peak = a;
      if (this.n === this.buf.length) {           // 40 ms at 8 kHz
        this.port.postMessage({ pcm: this.buf.buffer, peak: this.peak }, [this.buf.buffer]);
        this.buf = new Int16Array(320); this.n = 0; this.peak = 0;
      }
    }
    return true;
  }
}
registerProcessor('sim-capture', SimCapture);
`

const PLAYER = `
class SimPlayer extends AudioWorkletProcessor {
  constructor() {
    super();
    this.step = 8000 / sampleRate;   // input (8 kHz) samples consumed per output sample
    this.q = []; this.cur = null; this.pos = 0; this.frac = 0; this.prev = 0; this.nxt = 0; this.queued = 0;
    this.maxQueued = 8000 * 0.6;     // keep latency bounded: drop audio beyond 600 ms
    this.peak = 0; this.sinceReport = 0;
    this.port.onmessage = (e) => {
      if (e.data === 'flush') { this.q = []; this.cur = null; this.queued = 0; return; }
      const s = new Int16Array(e.data);
      this.q.push(s); this.queued += s.length;
      while (this.queued > this.maxQueued && this.q.length > 1) this.queued -= this.q.shift().length;
    };
  }
  next() {   // next 8 kHz sample as float, 0 on underrun
    if (!this.cur || this.pos >= this.cur.length) {
      this.cur = this.q.shift() || null; this.pos = 0;
      if (!this.cur) return 0;
      this.queued -= this.cur.length;
    }
    return this.cur[this.pos++] / 0x8000;
  }
  process(_, outputs) {
    const out = outputs[0][0];
    for (let i = 0; i < out.length; i++) {
      this.frac += this.step;
      while (this.frac >= 1) { this.prev = this.nxt; this.nxt = this.next(); this.frac -= 1; }
      const v = this.prev + (this.nxt - this.prev) * this.frac;   // linear interpolation
      out[i] = v;
      const a = v < 0 ? -v : v; if (a > this.peak) this.peak = a;
    }
    if ((this.sinceReport += out.length) >= sampleRate / 10) {   // ~10 reports/s
      this.port.postMessage({ peak: this.peak }); this.peak = 0; this.sinceReport = 0;
    }
    return true;
  }
}
registerProcessor('sim-player', SimPlayer);
`

const moduleUrl = (src) => URL.createObjectURL(new Blob([src], { type: 'application/javascript' }))

// startSimAudio({ onPcm, onMicLevel, onBotLevel }) → { play(arrayBuffer), flush(), setMuted(bool), stop() }
export async function startSimAudio({ onPcm, onMicLevel, onBotLevel }) {
    if (!window.AudioWorkletNode) throw new Error('This browser has no AudioWorklet support — use a current Chrome, Edge or Firefox.')
    if (!navigator.mediaDevices?.getUserMedia) throw new Error('Microphone access needs HTTPS (or localhost).')
    const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 }
    })
    const ctx = new AudioContext({ latencyHint: 'interactive' })
    try {
        const urls = [moduleUrl(CAPTURE), moduleUrl(PLAYER)]
        await Promise.all(urls.map(u => ctx.audioWorklet.addModule(u)))
        urls.forEach(u => URL.revokeObjectURL(u))
    } catch (e) {
        stream.getTracks().forEach(t => t.stop()); ctx.close().catch(() => {})
        throw e
    }

    const mic = ctx.createMediaStreamSource(stream)
    const capture = new AudioWorkletNode(ctx, 'sim-capture', { numberOfInputs: 1, numberOfOutputs: 0 })
    let muted = false
    capture.port.onmessage = (e) => {
        onMicLevel && onMicLevel(muted ? 0 : e.data.peak)
        // While muted, send silence so the bot still gets a steady 8 kHz stream.
        onPcm(muted ? new Int16Array(320).buffer : e.data.pcm)
    }
    mic.connect(capture)

    const player = new AudioWorkletNode(ctx, 'sim-player', { numberOfInputs: 0, numberOfOutputs: 1, outputChannelCount: [1] })
    player.port.onmessage = (e) => onBotLevel && onBotLevel(e.data.peak)
    player.connect(ctx.destination)
    if (ctx.state === 'suspended') await ctx.resume()

    return {
        sampleRate: ctx.sampleRate,
        play: (buf) => player.port.postMessage(buf, [buf]),
        flush: () => player.port.postMessage('flush'),
        setMuted: (m) => { muted = !!m },
        stop: () => {
            try { mic.disconnect(); capture.disconnect(); player.disconnect() } catch (_) {}
            stream.getTracks().forEach(t => t.stop())
            ctx.close().catch(() => {})
        }
    }
}
